import React, { useState, useEffect } from "react";
import {
  Zap,
  Home,
  Search,
  User,
  Settings,
  Menu,
  Bell,
  X,
  TrendingUp,
  BookOpen,
  CreditCard,
  Gift,
  Shield,
  Sun,
  Moon,
  Ticket,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MyBetsContent from "./MyBetsContent";
import LiveSportsContent from "./LiveSportsContent";
import ExploreContent from "./ExploreContent";
import SportsContent from "./SportsContent";
import DepositContent from "./DepositContent";
import HomeContent from "./HomeContent";
import useUserData from "../../Hooks/useUserData";
import FootballLoading from "../../Utils/FootballLoading";


// App Component
export default function BettingApp() {
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [showNotifications, setShowNotifications] = useState(false);
  const { userData, loading, error, refreshUserData } = useUserData();

  // Contents for each main navigation tab
  const tabContents = {
    home: <HomeContent />,
    liveSports: <LiveSportsContent />,
    sports: <SportsContent />,
    myBets: <MyBetsContent />,
    explore: <ExploreContent />,
    deposit: <DepositContent />,
  };

    if (loading) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
          <FootballLoading />
          <p className="mt-4 text-white text-opacity-80 animate-pulse">
            Cargando...
          </p>
        </div>
      );
    }

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } min-h-screen font-sans`}
    >
      {/* Header */}
      <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
        <div className="flex items-center">
          <button onClick={() => setIsOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="ml-3 font-bold text-lg">BetSport</h1>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => setShowNotifications(!showNotifications)}>
            <Bell className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 bg-red-700 px-2 py-1 rounded-md">
            <User className="w-5 h-5" />
            <span className="text-sm font-medium">{userData?.saldo || 0}</span>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              className="fixed top-0 left-0 bottom-0 w-64 bg-gray-800 z-50"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25 }}
            >
              <div className="p-4 flex justify-between items-center border-b border-gray-700">
                <h2 className="text-white font-bold text-lg">Menú</h2>
                <button onClick={() => setIsOpen(false)}>
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <button
                    className="flex items-center text-white w-full"
                    onClick={() => {
                      setActiveTab("home");
                      setIsOpen(false);
                    }}
                  >
                    <Home className="h-5 w-5 mr-2" />
                    <span>Inicio</span>
                  </button>
                  <button
                    className="flex items-center text-white w-full"
                    onClick={() => {
                      setActiveTab("liveSports");
                      setIsOpen(false);
                    }}
                  >
                    <Zap className="h-5 w-5 mr-2" />
                    <span>Deportes en vivo</span>
                  </button>
                  <button
                    className="flex items-center text-white w-full"
                    onClick={() => {
                      setActiveTab("sports");
                      setIsOpen(false);
                    }}
                  >
                    <TrendingUp className="h-5 w-5 mr-2" />
                    <span>Deportes</span>
                  </button>
                  <button
                    className="flex items-center text-white w-full"
                    onClick={() => {
                      setActiveTab("myBets");
                      setIsOpen(false);
                    }}
                  >
                    <BookOpen className="h-5 w-5 mr-2" />
                    <span>Mis Apuestas</span>
                  </button>
                  <button
                    className="flex items-center text-white w-full"
                    onClick={() => {
                      setActiveTab("explore");
                      setIsOpen(false);
                    }}
                  >
                    <Search className="h-5 w-5 mr-2" />
                    <span>Explorar</span>
                  </button>
                  <button
                    className="flex items-center text-white w-full"
                    onClick={() => {
                      setActiveTab("deposit");
                      setIsOpen(false);
                    }}
                  >
                    <CreditCard className="h-5 w-5 mr-2" />
                    <span>Depósito/Retiro</span>
                  </button>
                </div>

                <div className="mt-8 pt-4 border-t border-gray-700">
                  <button
                    className="flex items-center text-white w-full mb-3"
                    onClick={() => setDarkMode(!darkMode)}
                  >
                    {darkMode ? (
                      <Sun className="h-5 w-5 mr-2" />
                    ) : (
                      <Moon className="h-5 w-5 mr-2" />
                    )}
                    <span>{darkMode ? "Modo claro" : "Modo oscuro"}</span>
                  </button>
                  <button className="flex items-center text-white w-full">
                    <Settings className="h-5 w-5 mr-2" />
                    <span>Configuración</span>
                  </button>
                  <button className="flex items-center text-white w-full mt-3">
                    <Shield className="h-5 w-5 mr-2" />
                    <span>Juego Responsable</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Notifications Panel */}
      <AnimatePresence>
        {showNotifications && (
          <>
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNotifications(false)}
            />
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-64 bg-gray-800 z-50"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
            >
              <div className="p-4 flex justify-between items-center border-b border-gray-700">
                <h2 className="text-white font-bold text-lg">Notificaciones</h2>
                <button onClick={() => setShowNotifications(false)}>
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="flex items-start">
                    <Ticket className="w-5 h-5 text-orange-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-white text-sm">
                        ¡Tu apuesta fue ganadora! Recibiste $112.50
                      </p>
                      <span className="text-gray-400 text-xs">
                        Hace 5 minutos
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="flex items-start">
                    <Gift className="w-5 h-5 text-orange-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-white text-sm">
                        Nuevo bono disponible: 50% en tu próximo depósito
                      </p>
                      <span className="text-gray-400 text-xs">
                        Hace 2 horas
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="flex items-start">
                    <Zap className="w-5 h-5 text-orange-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-white text-sm">
                        River vs Boca está por comenzar
                      </p>
                      <span className="text-gray-400 text-xs">
                        Hace 6 horas
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="pb-16">{tabContents[activeTab]}</div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-1 flex justify-around">
        <motion.button
          className={`flex flex-col items-center py-1 px-2 ${
            activeTab === "home" ? "text-orange-500" : "text-gray-400"
          }`}
          onClick={() => setActiveTab("home")}
          whileTap={{ scale: 0.9 }}
        >
          <Home className="w-5 h-5" />
          <span className="text-xs mt-1">Inicio</span>
        </motion.button>
        <motion.button
          className={`flex flex-col items-center py-1 px-2 ${
            activeTab === "liveSports" ? "text-orange-500" : "text-gray-400"
          }`}
          onClick={() => setActiveTab("liveSports")}
          whileTap={{ scale: 0.9 }}
        >
          <Zap className="w-5 h-5" />
          <span className="text-xs mt-1">En vivo</span>
        </motion.button>
        <motion.button
          className={`flex flex-col items-center py-1 px-2 ${
            activeTab === "myBets" ? "text-orange-500" : "text-gray-400"
          }`}
          onClick={() => setActiveTab("myBets")}
          whileTap={{ scale: 0.9 }}
        >
          <BookOpen className="w-5 h-5" />
          <span className="text-xs mt-1">Apuestas</span>
        </motion.button>
        <motion.button
          className={`flex flex-col items-center py-1 px-2 ${
            activeTab === "explore" ? "text-orange-500" : "text-gray-400"
          }`}
          onClick={() => setActiveTab("explore")}
          whileTap={{ scale: 0.9 }}
        >
          <Search className="w-5 h-5" />
          <span className="text-xs mt-1">Explorar</span>
        </motion.button>
      </div>
    </div>
  );
}